import { select, takeLatest, call, put } from 'redux-saga/effects';
import {
    getAllStudiesSuccess,
    getAllStudiesFailure,
    getOrdersForLinkingSuccess,
    getOrdersForLinkingFailure,
    linkStudySuccess,
    linkStudyFailure,
    quickCreateOrderSuccess,
    quickCreateOrderFailure,
    ignoreStudySuccess,
    ignoreStudyFailure,
} from './slice';
import requestHandler from '../../../utils/requestHandler';
import { logoutRequest } from '../auth/authSlice';

const API_BASE = process.env.REACT_APP_SERVER_API;

function* getAllStudiesRequest(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }

        const currentBranch = yield select(state => state.branch.currentBranch);
        if (!currentBranch?.id) {
            // Branch not loaded yet, skip fetch — will be retried when branch loads
            return;
        }
        const { page = 1, limit = 20, dateFrom, dateTo, patientName, status } = action.payload || {};

        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        if (patientName) params.append('patientName', patientName);
        if (status) params.append('status', status);

        const requestDetail = {
            method: "GET",
            headers: {
                "Authorization": authToken,
                "Content-Type": "application/json",
                "X-Current-Branch": currentBranch.id
            }
        };

        const response = yield call(
            requestHandler,
            `${API_BASE}api/dicom/all-studies?${params.toString()}`,
            requestDetail
        );

        yield put(getAllStudiesSuccess(response));
    } catch (error) {
        console.error('getAllStudies error:', error);
        yield put(getAllStudiesFailure(error?.message || 'Failed to fetch studies'));
    }
}

function* getOrdersForLinkingRequest(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }

        const currentBranch = yield select(state => state.branch.currentBranch);
        const { search = '', page = 1, limit = 20 } = action.payload || {};

        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);

        const requestDetail = {
            method: "GET",
            headers: {
                "Authorization": authToken,
                "Content-Type": "application/json",
                "X-Current-Branch": currentBranch?.id
            }
        };

        const response = yield call(
            requestHandler,
            `${API_BASE}api/dicom/orders-for-linking?${params.toString()}`,
            requestDetail
        );

        yield put(getOrdersForLinkingSuccess(response));
    } catch (error) {
        console.error('getOrdersForLinking error:', error);
        yield put(getOrdersForLinkingFailure(error?.message || 'Failed to fetch orders'));
    }
}

function* linkStudyRequest(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }

        const currentBranch = yield select(state => state.branch.currentBranch);
        const { orthancStudyId, orderId } = action.payload;

        const requestDetail = {
            method: "POST",
            headers: {
                "Authorization": authToken,
                "Content-Type": "application/json",
                "X-Current-Branch": currentBranch?.id
            },
            body: JSON.stringify({ orthancStudyId, orderId })
        };

        const response = yield call(
            requestHandler,
            `${API_BASE}api/dicom/link-orthanc-study`,
            requestDetail
        );

        yield put(linkStudySuccess(response));
    } catch (error) {
        console.error('linkStudy error:', error);
        yield put(linkStudyFailure({ success: false, message: error?.message || 'Failed to link study' }));
    }
}

function* quickCreateOrderRequest(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }

        const currentBranch = yield select(state => state.branch.currentBranch);

        const requestDetail = {
            method: "POST",
            headers: {
                "Authorization": authToken,
                "Content-Type": "application/json",
                "X-Current-Branch": currentBranch?.id
            },
            body: JSON.stringify(action.payload)
        };

        const response = yield call(
            requestHandler,
            `${API_BASE}api/dicom/quick-create-order`,
            requestDetail
        );

        yield put(quickCreateOrderSuccess(response));
    } catch (error) {
        console.error('quickCreateOrder error:', error);
        yield put(quickCreateOrderFailure({ success: false, message: error?.message || 'Failed to create order' }));
    }
}

function* ignoreStudyRequest(action) {
    try {
        const authToken = localStorage.getItem("item");
        if (!authToken) {
            yield put(logoutRequest());
            return;
        }

        const { orthancStudyId, reason, unignore } = action.payload;
        const endpoint = unignore ? 'unignore' : 'ignore';

        const requestDetail = {
            method: "PUT",
            headers: {
                "Authorization": authToken,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ reason })
        };

        const response = yield call(
            requestHandler,
            `${API_BASE}api/dicom/studies/${orthancStudyId}/${endpoint}`,
            requestDetail
        );

        yield put(ignoreStudySuccess(response));
    } catch (error) {
        console.error('ignoreStudy error:', error);
        yield put(ignoreStudyFailure({ success: false, message: error?.message || 'Failed to update study status' }));
    }
}

export default function* allStudiesSaga() {
    yield takeLatest('allStudies/getAllStudiesRequest', getAllStudiesRequest);
    yield takeLatest('allStudies/getOrdersForLinkingRequest', getOrdersForLinkingRequest);
    yield takeLatest('allStudies/linkStudyRequest', linkStudyRequest);
    yield takeLatest('allStudies/quickCreateOrderRequest', quickCreateOrderRequest);
    yield takeLatest('allStudies/ignoreStudyRequest', ignoreStudyRequest);
}
